import type { QueryParams } from '@sanity/client';

// Stable, deterministic key for a query/params pair so SSR and client share data.
function buildKey(query: string, params?: QueryParams) {
    const paramPart = params ? JSON.stringify(params) : '';
    return `sanity:${query}::${paramPart}`;
}

export async function useSanityQuery<T = any>(query: string, params?: QueryParams) {
    const key = buildKey(query, params);

    // useAsyncData ensures the SSR result is serialized into the Nuxt payload
    // and reused on the client, preventing the extra client-side fetch that
    // briefly removes SEO-critical content (H2's, meta, copy) during hydration.
    //
    // The Sanity client itself is loaded lazily inside the handler so the
    // ~37 KiB @sanity/client runtime is not pulled into the entry bundle on
    // routes that don't actually call useSanityQuery during SSR/hydration.
    const { data, error, pending, refresh, status } = await useAsyncData<T | null>(
        key,
        async () => {
            try {
                const client = await useSanityClient();
                return await client.fetch<T>(query, params || {});
            } catch (e) {
                console.error('[useSanityQuery] Error fetching data:', e);
                throw e;
            }
        },
        {
            default: () => null,
        },
    );

    return {
        data,
        error,
        pending,
        refresh,
        status,
    };
}

export function groq(strings: TemplateStringsArray, ...values: any[]): string {
    return strings.reduce((acc, str, i) => acc + str + (values[i] ?? ''), '');
}
